import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClockIcon, FireIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface CodeSnippet {
  id: string;
  title: string;
  language: string;
  code: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
}

const codeSnippets: CodeSnippet[] = [
  {
    id: 'react-hook',
    title: 'React useState Hook',
    language: 'JavaScript',
    difficulty: 'Beginner',
    code: `const [count, setCount] = useState(0);
const increment = () => setCount(count + 1);
return <button onClick={increment}>{count}</button>;`
  },
  {
    id: 'python-list-comp',
    title: 'Python List Comprehension',
    language: 'Python',
    difficulty: 'Intermediate',
    code: `squares = [x**2 for x in range(10) if x % 2 == 0]
filtered_data = [item.upper() for item in data if len(item) > 3]
result = {k: v for k, v in dict.items() if v is not None}`
  },
  {
    id: 'async-await',
    title: 'Async/Await Pattern',
    language: 'JavaScript',
    difficulty: 'Advanced',
    code: `async function fetchUserData(userId) {
  try {
    const response = await fetch(\`/api/users/\${userId}\`);
    const userData = await response.json();
    return userData;
  } catch (error) {
    console.error('Failed to fetch user:', error);
    throw error;
  }
}`
  },
  {
    id: 'sql-join',
    title: 'SQL Complex Join',
    language: 'SQL',
    difficulty: 'Advanced',
    code: `SELECT u.name, p.title, c.content, COUNT(l.id) as likes
FROM users u
INNER JOIN posts p ON u.id = p.user_id
LEFT JOIN comments c ON p.id = c.post_id
LEFT JOIN likes l ON p.id = l.post_id
WHERE u.active = true AND p.published_at > '2024-01-01'
GROUP BY u.id, p.id, c.id
ORDER BY likes DESC, p.created_at DESC;`
  },
  {
    id: 'css-grid',
    title: 'CSS Grid Layout',
    language: 'CSS',
    difficulty: 'Intermediate',
    code: `.container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-gap: 2rem;
  align-items: start;
}

.item {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}`
  }
];

interface TypingStats {
  wpm: number;
  accuracy: number;
  timeElapsed: number;
  correctChars: number;
  totalChars: number;
  errors: number;
}

export default function TypingChallenge() {
  const [selectedSnippet, setSelectedSnippet] = useState(codeSnippets[0]);
  const [userInput, setUserInput] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [stats, setStats] = useState<TypingStats>({
    wpm: 0,
    accuracy: 0,
    timeElapsed: 0,
    correctChars: 0,
    totalChars: 0,
    errors: 0
  });
  const [isCompleted, setIsCompleted] = useState(false);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isActive && startTime) {
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const timeElapsed = (now - startTime) / 1000;
        const wordsTyped = userInput.length / 5; // Standard: 5 characters = 1 word
        const wpm = Math.round((wordsTyped / timeElapsed) * 60);
        
        const correctChars = calculateCorrectChars();
        const accuracy = userInput.length > 0 ? Math.round((correctChars / userInput.length) * 100) : 100;
        const errors = userInput.length - correctChars;

        setStats({
          wpm: isNaN(wpm) ? 0 : wpm,
          accuracy,
          timeElapsed,
          correctChars,
          totalChars: userInput.length,
          errors
        });
      }, 100);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, startTime, userInput]);

  useEffect(() => {
    if (userInput === selectedSnippet.code && userInput.length > 0) {
      setIsCompleted(true);
      setIsActive(false);
    }
  }, [userInput, selectedSnippet.code]);

  const calculateCorrectChars = () => {
    let correct = 0;
    for (let i = 0; i < userInput.length; i++) {
      if (userInput[i] === selectedSnippet.code[i]) {
        correct++;
      }
    }
    return correct;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    
    if (!isActive && value.length > 0) {
      setIsActive(true);
      setStartTime(Date.now());
    }

    setUserInput(value);
    setCurrentCharIndex(value.length);
  };

  const resetChallenge = () => {
    setUserInput('');
    setIsActive(false);
    setStartTime(null);
    setIsCompleted(false);
    setCurrentCharIndex(0);
    setStats({
      wpm: 0,
      accuracy: 0,
      timeElapsed: 0,
      correctChars: 0,
      totalChars: 0,
      errors: 0
    });
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const selectSnippet = (snippet: CodeSnippet) => {
    setSelectedSnippet(snippet);
    resetChallenge();
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-400 bg-green-400/10';
      case 'Intermediate': return 'text-yellow-400 bg-yellow-400/10';
      case 'Advanced': return 'text-red-400 bg-red-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  const renderCodeWithHighlight = () => {
    const code = selectedSnippet.code;
    return code.split('').map((char, index) => {
      let className = 'transition-all duration-75 ';
      
      if (index < userInput.length) {
        if (userInput[index] === char) {
          className += 'bg-green-500/20 text-green-300';
        } else {
          className += 'bg-red-500/20 text-red-300';
        }
      } else if (index === currentCharIndex) {
        className += 'bg-blue-500/30 animate-pulse';
      } else {
        className += 'text-gray-300';
      }

      return (
        <span key={index} className={className}>
          {char === '\n' ? <br /> : char === ' ' ? '\u00A0' : char}
        </span>
      );
    });
  };

  const getWPMColor = (wpm: number) => {
    if (wpm >= 80) return 'text-green-400';
    if (wpm >= 60) return 'text-yellow-400';
    if (wpm >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 95) return 'text-green-400';
    if (accuracy >= 90) return 'text-yellow-400';
    if (accuracy >= 80) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <section id="typing" className="py-20 bg-gradient-to-br from-purple-900 via-gray-900 to-indigo-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Code Typing <span className="bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">Challenge</span>
          </h2>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">
            Test your typing speed with real code snippets. Perfect way to demonstrate both coding familiarity and typing proficiency!
          </p>
          <div className="w-20 h-1 bg-gradient-to-r from-purple-300 to-indigo-300 mx-auto rounded-full mt-4"></div>
        </motion.div>

        {/* Stats Dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-600">
            <div className="flex items-center space-x-2 mb-2">
              <FireIcon className="w-5 h-5 text-orange-400" />
              <span className="text-gray-300 text-sm">WPM</span>
            </div>
            <div className={`text-2xl font-bold ${getWPMColor(stats.wpm)}`}>
              {stats.wpm}
            </div>
          </div>
          
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-600">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircleIcon className="w-5 h-5 text-green-400" />
              <span className="text-gray-300 text-sm">Accuracy</span>
            </div>
            <div className={`text-2xl font-bold ${getAccuracyColor(stats.accuracy)}`}>
              {stats.accuracy}%
            </div>
          </div>
          
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-600">
            <div className="flex items-center space-x-2 mb-2">
              <ClockIcon className="w-5 h-5 text-blue-400" />
              <span className="text-gray-300 text-sm">Time</span>
            </div>
            <div className="text-2xl font-bold text-blue-400">
              {stats.timeElapsed.toFixed(1)}s
            </div>
          </div>
          
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-600">
            <div className="flex items-center space-x-2 mb-2">
              <XCircleIcon className="w-5 h-5 text-red-400" />
              <span className="text-gray-300 text-sm">Errors</span>
            </div>
            <div className="text-2xl font-bold text-red-400">
              {stats.errors}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Code Snippet Selector */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-4"
          >
            <h3 className="text-xl font-semibold text-white mb-4">Choose Code Snippet</h3>
            {codeSnippets.map((snippet) => (
              <motion.button
                key={snippet.id}
                onClick={() => selectSnippet(snippet)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full p-4 rounded-xl border transition-all duration-300 text-left ${
                  selectedSnippet.id === snippet.id
                    ? 'border-purple-400 bg-purple-400/10'
                    : 'border-gray-600 bg-gray-800/50 hover:border-purple-400/50'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-white text-sm">{snippet.title}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(snippet.difficulty)}`}>
                    {snippet.difficulty}
                  </span>
                </div>
                <p className="text-gray-400 text-xs">{snippet.language}</p>
              </motion.button>
            ))}
          </motion.div>

          {/* Typing Area */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="lg:col-span-3 space-y-6"
          >
            {/* Code Display */}
            <div className="bg-gray-800/50 rounded-xl border border-gray-600 overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-600">
                <div>
                  <h3 className="text-lg font-semibold text-white">{selectedSnippet.title}</h3>
                  <p className="text-gray-400 text-sm">{selectedSnippet.language}</p>
                </div>
                <button
                  onClick={resetChallenge}
                  className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg text-sm hover:bg-purple-500/30 transition-colors"
                >
                  🔄 Reset
                </button>
              </div>
              
              <div className="p-6">
                <div className="bg-gray-900/50 rounded-lg p-4 mb-4 font-mono text-sm leading-relaxed overflow-x-auto">
                  <pre className="whitespace-pre-wrap">{renderCodeWithHighlight()}</pre>
                </div>
                
                <textarea
                  ref={inputRef}
                  value={userInput}
                  onChange={handleInputChange}
                  className="w-full h-32 bg-gray-900/50 text-white font-mono text-sm p-4 rounded-lg border border-gray-600 focus:border-purple-400 focus:outline-none resize-none"
                  placeholder="Start typing the code above..."
                  spellCheck={false}
                  disabled={isCompleted}
                />
              </div>
            </div>

            {/* Completion Message */}
            <AnimatePresence>
              {isCompleted && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30 rounded-xl p-6 text-center"
                >
                  <div className="text-4xl mb-4">🎉</div>
                  <h3 className="text-2xl font-bold text-green-400 mb-2">Challenge Completed!</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <div className="text-2xl font-bold text-green-400">{stats.wpm}</div>
                      <div className="text-green-300 text-sm">WPM</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-400">{stats.accuracy}%</div>
                      <div className="text-green-300 text-sm">Accuracy</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-400">{stats.timeElapsed.toFixed(1)}s</div>
                      <div className="text-green-300 text-sm">Time</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-400">{stats.errors}</div>
                      <div className="text-green-300 text-sm">Errors</div>
                    </div>
                  </div>
                  <button
                    onClick={resetChallenge}
                    className="mt-4 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-semibold rounded-lg transition-all duration-300"
                  >
                    Try Another Challenge
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}